/**
 * Display metadata for prospect concepts — temperatures, stages, categories.
 * Single source of truth for colours, emoji and i18n label KEYS across the
 * Audit page, the CRM pipeline and the dashboard.
 *
 * Labels are i18n keys resolved at render time via t() — never hardcoded
 * strings — so every consumer translates with the active UI language.
 */
import type { Temperature, Stage, ProspectCategory } from '@/hooks/useProspect';

export interface TempMeta {
  /** i18n key — resolve with t(). */
  labelKey: string;
  /** Tailwind-ready hex for accents. */
  color: string;
  emoji: string;
}

export const TEMPERATURE_META: Record<Temperature, TempMeta> = {
  hot: { labelKey: 'pipeline.stages.hot', color: '#ec4899', emoji: '🔥' },
  warm: { labelKey: 'pipeline.stages.warm', color: '#f59e0b', emoji: '🌤️' },
  cold: { labelKey: 'pipeline.stages.cold', color: '#3b82f6', emoji: '❄️' },
};

export interface StageMeta {
  /** i18n key — resolve with t(). */
  labelKey: string;
  color: string;
  /** i18n key for the short helper shown under the column title. */
  hintKey: string;
}

/** Pipeline stages in board order. */
export const STAGE_ORDER: Stage[] = ['cold', 'warm', 'hot', 'meeting', 'client', 'lost'];

export const STAGE_META: Record<Stage, StageMeta> = {
  cold: { labelKey: 'pipeline.stages.cold', color: '#3b82f6', hintKey: 'prospect.stageHint.cold' },
  warm: { labelKey: 'pipeline.stages.warm', color: '#f59e0b', hintKey: 'prospect.stageHint.warm' },
  hot: { labelKey: 'pipeline.stages.hot', color: '#ec4899', hintKey: 'prospect.stageHint.hot' },
  meeting: { labelKey: 'pipeline.stages.meeting', color: '#a855f7', hintKey: 'prospect.stageHint.meeting' },
  client: { labelKey: 'pipeline.stages.client', color: '#10b981', hintKey: 'prospect.stageHint.client' },
  lost: { labelKey: 'pipeline.stages.lost', color: '#94a3b8', hintKey: 'prospect.stageHint.lost' },
};

export const CATEGORY_META: Record<ProspectCategory, { labelKey: string; emoji: string }> = {
  jeune_actif: { labelKey: 'prospect.category.jeune_actif', emoji: '🚀' },
  parent: { labelKey: 'prospect.category.parent', emoji: '👨‍👩‍👧' },
  entrepreneur: { labelKey: 'prospect.category.entrepreneur', emoji: '💼' },
  voyage: { labelKey: 'prospect.category.voyage', emoji: '✈️' },
  sportif: { labelKey: 'prospect.category.sportif', emoji: '🏃' },
};

/** Colour for a 0-100 score, on the cold→hot gradient. */
export function scoreColor(score: number): string {
  if (score >= 70) return '#ec4899';
  if (score >= 45) return '#f59e0b';
  return '#3b82f6';
}
